/**
 * Runtime configuration, read from /config.json at startup. In Azure the
 * container writes it from env vars (RPF-182/RPF-183); locally it is
 * public/config.json. Nothing here is secret: the Entra client and tenant ids
 * are public SPA config (RPF-177).
 */
export interface IHostConfig {
  planServiceApiBaseUrl: string;
  planServiceScope: string;
  /** Application (client) id of the host's own Entra SPA registration (RPF-177). */
  msalClientId: string;
  /** Entra tenant id; the authority is login.microsoftonline.com/<tenant>. */
  msalTenantId: string;
  /** Defaults to the page origin when absent. */
  msalRedirectUri?: string;
}

type RequiredKey = Exclude<keyof IHostConfig, 'msalRedirectUri'>;

const REQUIRED_KEYS: RequiredKey[] = [
  'planServiceApiBaseUrl',
  'planServiceScope',
  'msalClientId',
  'msalTenantId',
];

export async function loadRuntimeConfig(fetchImpl: typeof fetch = fetch): Promise<IHostConfig> {
  const response = await fetchImpl('/config.json', { cache: 'no-store' });
  if (!response.ok) {
    throw new Error(`Could not load /config.json (HTTP ${response.status})`);
  }

  const raw = (await response.json()) as Partial<Record<keyof IHostConfig, unknown>>;
  const missing = REQUIRED_KEYS.filter((key) => {
    const value = raw[key];
    return typeof value !== 'string' || value.trim() === '';
  });
  if (missing.length > 0) {
    throw new Error(`/config.json is missing: ${missing.join(', ')}`);
  }

  const redirectUri = raw.msalRedirectUri;
  return {
    planServiceApiBaseUrl: String(raw.planServiceApiBaseUrl),
    planServiceScope: String(raw.planServiceScope),
    msalClientId: String(raw.msalClientId),
    msalTenantId: String(raw.msalTenantId),
    ...(typeof redirectUri === 'string' && redirectUri.trim() !== ''
      ? { msalRedirectUri: redirectUri }
      : {}),
  };
}
